import "./App.css";
import "./gnv3-overrides.css";
import appIcon from "./images/favicon.png";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Outlet } from "react-router-dom";
import SiteLayout from "./site/SiteLayout";
import PageIntrouvable from "./pages/PageIntrouvable";
import Home from "./pages/Home";
import DeleteAccount from "./pages/DeleteAccount";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CoachMode from "./pages/CoachMode";
import ProUpgrade from "./pages/ProUpgrade";
import AdminDashboard from "./pages/AdminDashboard";
import Invite from "./pages/Invite";
import EmailConfirmed from "./pages/EmailConfirmed";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import LegalNotice from "./pages/LegalNotice";
import React, { useState, useEffect, useCallback } from "react";
import { isLoggedIn, getUser, logout, getCurrentSubscription, getUnreadMessageCount } from "./services/authService";
import MessagingModal from "./components/MessagingModal";

function Header() {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [loggedIn, setLoggedIn] = useState(false);
	const [isProUser, setIsProUser] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [showMessaging, setShowMessaging] = useState(false);
	const [messagingInitialPartner, setMessagingInitialPartner] = useState(null);

	useEffect(() => {
		const onOpen = (e) => {
			const detail = e.detail || {};
			if (detail.partnerId) {
				setMessagingInitialPartner({
					partnerId: detail.partnerId,
					partnerPrenom: detail.partnerPrenom || "",
					partnerNom: detail.partnerNom || "",
					partnerPhoto: detail.partnerPhoto || null,
				});
			} else {
				setMessagingInitialPartner(null);
			}
			setShowMessaging(true);
		};
		window.addEventListener("open-messaging", onOpen);
		return () => window.removeEventListener("open-messaging", onOpen);
	}, []);

	// Rafraîchit le compteur de non-lus depuis l'API. Exposé pour être appelé
	// immédiatement après ouverture d'une conversation (sinon le badge reste
	// jusqu'au prochain poll de 30s).
	const refreshUnreadCount = useCallback(async () => {
		if (!isLoggedIn()) return;
		const result = await getUnreadMessageCount();
		if (result.success) {
			setUnreadCount(result.unreadCount || 0);
		}
	}, []);

	useEffect(() => {
		let subscriptionChecked = false;
		let unreadInterval = null;

		const fetchUnreadCount = refreshUnreadCount;

		const checkAuth = () => {
			const logged = isLoggedIn();
			setLoggedIn(logged);
			if (logged) {
				setUser(getUser());
				// Check subscription once when login is detected
				if (!subscriptionChecked) {
					subscriptionChecked = true;
					checkSubscription();
					fetchUnreadCount();
					// Poll unread count every 30s while logged in
					unreadInterval = setInterval(fetchUnreadCount, 30000);
				}
			} else {
				setUser(null);
				setIsProUser(false);
				setUnreadCount(0);
				subscriptionChecked = false;
				if (unreadInterval) {
					clearInterval(unreadInterval);
					unreadInterval = null;
				}
			}
		};

		const checkSubscription = async () => {
			if (isLoggedIn()) {
				const result = await getCurrentSubscription();
				if (result.success && result.subscription) {
					const proTierIds = [4, 6];
					setIsProUser(proTierIds.includes(result.subscription.idAbonnementType));
				} else {
					setIsProUser(false);
				}
			}
		};

		checkAuth();
		// Re-check on storage change (login/logout in other tabs)
		window.addEventListener("storage", checkAuth);
		// Re-check periodically for same-tab updates
		const interval = setInterval(checkAuth, 1000);

		return () => {
			window.removeEventListener("storage", checkAuth);
			clearInterval(interval);
			if (unreadInterval) clearInterval(unreadInterval);
		};
	}, []);

	const handleLogout = () => {
		logout();
		setLoggedIn(false);
		setUser(null);
		navigate("/");
	};

	return (
		<>
			<header>
				<Link to="/" className="header-logo-link">
					<img src={appIcon} alt="GymNote" className="header-logo-icon" />
					<span className="header-logo-text">GymNote</span>
				</Link>
				<nav className="header-actions">
					{loggedIn && user ? (
						<div className="header-user-section">
							<Link to={isProUser ? "/coach" : "/pro"} className="header-dashboard-link">
								<span>Dashboard web</span>
								{!isProUser && (
									<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
										<path d="M12 2l2.09 4.9L19 8l-4 3.5L16.5 17 12 14.5 7.5 17 9 11.5 5 8l4.91-1.1L12 2z" opacity="0.85"/>
									</svg>
								)}
							</Link>
							<button
								className="header-icon-btn header-messaging-btn"
								onClick={() => setShowMessaging(true)}
								title="Messagerie"
								aria-label="Messagerie"
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
								</svg>
								{unreadCount > 0 && (
									<span className="header-messaging-badge">
										{unreadCount > 99 ? "99+" : unreadCount}
									</span>
								)}
							</button>
							<Link to="/dashboard" className="header-user-name" title="Mon compte">
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
									<circle cx="12" cy="7" r="4" />
								</svg>
								<span>{user.prenom} {user.nom}</span>
							</Link>
							{Array.isArray(user.adminRoles) && user.adminRoles.includes("admin") && (
								<Link to="/admin" className="header-admin-chip" title="Interface admin">
									Admin
								</Link>
							)}
							<button
								onClick={handleLogout}
								className="header-icon-btn header-logout-btn"
								title="Se déconnecter"
								aria-label="Se déconnecter"
							>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
									<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
									<polyline points="16 17 21 12 16 7" />
									<line x1="21" y1="12" x2="9" y2="12" />
								</svg>
							</button>
						</div>
					) : (
						<Link to="/login" className="header-login-link">
							Connexion
						</Link>
					)}
				</nav>
			</header>
			<MessagingModal
				visible={showMessaging}
				onClose={() => { setShowMessaging(false); setMessagingInitialPartner(null); refreshUnreadCount(); }}
				onMessagesRead={refreshUnreadCount}
				initialPartner={messagingInitialPartner}
			/>
		</>
	);
}

// Espace connecté (compte, dashboard coach web, admin) : en-tête, main et pied
// de page d'origine, inchangés. Les pages publiques passent par SiteLayout.
function EspaceConnecte() {
	return (
		<div className="container">
			<Header />
			<main>
				<Outlet />
			</main>
			<footer>
				<p>Copyright {new Date().getFullYear()} Gymnote</p>
				<Link to="/delete-account" className="footer-link">Supprimer mon compte</Link>
				<Link to="/privacy" className="footer-link">Politique de confidentialité</Link>
				<Link to="/terms" className="footer-link">CGU</Link>
				<Link to="/legal-notice" className="footer-link">Mentions légales</Link>
			</footer>
		</div>
	);
}

function App() {
	return (
		<Router>
			<Routes>
				<Route element={<SiteLayout />}>
					<Route path="/" element={<Home />} />
					<Route path="/login" element={<Login />} />
					<Route path="/pro" element={<ProUpgrade />} />
					<Route path="/invite" element={<Invite />} />
					<Route path="/email-confirme" element={<EmailConfirmed />} />
					<Route path="/delete-account" element={<DeleteAccount />} />
					<Route path="/privacy" element={<Privacy />} />
					<Route path="/terms" element={<Terms />} />
					<Route path="/legal-notice" element={<LegalNotice />} />
					<Route path="*" element={<PageIntrouvable />} />
				</Route>
				<Route element={<EspaceConnecte />}>
					<Route path="/dashboard" element={<Dashboard />} />
					<Route path="/coach" element={<CoachMode />} />
					<Route path="/admin" element={<AdminDashboard />} />
				</Route>
			</Routes>
		</Router>
	);
}

export default App;
